<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { conversationUrl, frontendURL } from 'dashboard/helper/URLHelper';
import AutomationEventsAPI from 'dashboard/api/automationEvents';
import AutomationEventDetailModal from 'dashboard/components/widgets/automation/AutomationEventDetailModal.vue';
import {
  buildLocalizedTitle,
  expandJornadaItems,
  filterJornadaByCategory,
  formatJourneyDateTime,
  formatJourneyMoney,
  groupJornadaByDay,
  JORNADA_CATEGORIES,
  localizedJourneyActor,
  localizedJourneyCategory,
  localizedJourneyStatus,
  normalizeJourneyLocale,
} from './jornada360Adapter';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  items: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  isLoadingMore: {
    type: Boolean,
    default: false,
  },
  /** True when the Journey API still has pages beyond the loaded items. */
  hasMoreRemote: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: '',
  },
  contactName: {
    type: String,
    default: '',
  },
  summary: {
    type: Object,
    default: () => ({}),
  },
  /** When false, renders inline instead of teleporting to body (test-friendly). */
  teleport: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['update:modelValue', 'refresh', 'loadMore']);
const { t, locale } = useI18n();
const router = useRouter();
const { accountId } = useAccount();
const { isAdmin } = useAdmin();

const INITIAL_VISIBLE = 8;
const PAGE_SIZE = 8;

const activeCategory = ref('all');
const visibleCount = ref(INITIAL_VISIBLE);
const expandedIds = ref(new Set());
const isDetailOpen = ref(false);
const selectedEvent = ref(null);
const isDetailLoading = ref(false);
let activeDetailRequestId = 0;

const categories = computed(() =>
  JORNADA_CATEGORIES.map(key => ({
    key,
    label: localizedJourneyCategory(key, t),
    count:
      key === 'all'
        ? props.items.length
        : props.items.filter(item => item.category === key).length,
  }))
);

const filteredItems = computed(() =>
  filterJornadaByCategory(props.items, activeCategory.value)
);

const expansion = computed(() =>
  expandJornadaItems(filteredItems.value, visibleCount.value)
);

const dayGroups = computed(() => groupJornadaByDay(expansion.value.visible));

/** Local list still has hidden rows, or remote cursor has more pages. */
const canShowMore = computed(
  () => expansion.value.hasMore || props.hasMoreRemote
);

const close = () => emit('update:modelValue', false);

const onCategoryChange = key => {
  activeCategory.value = key;
  visibleCount.value = INITIAL_VISIBLE;
  expandedIds.value = new Set();
};

const showMore = () => {
  if (expansion.value.hasMore) {
    visibleCount.value += PAGE_SIZE;
    return;
  }
  if (props.hasMoreRemote && !props.isLoadingMore) {
    // After remote append, raise the local window so new rows appear.
    visibleCount.value = filteredItems.value.length + PAGE_SIZE;
    emit('loadMore');
  }
};

const toggleExpanded = id => {
  const next = new Set(expandedIds.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  expandedIds.value = next;
};

const isExpanded = id => expandedIds.value.has(id);

const statusLabel = status => localizedJourneyStatus(status, t);

const localizedTitle = item => buildLocalizedTitle(item, t, locale.value);

const statusClass = status => {
  const classes = {
    completed: 'bg-n-teal-3 text-n-teal-11',
    success: 'bg-n-teal-3 text-n-teal-11',
    failed: 'bg-n-ruby-3 text-n-ruby-11',
    manual_action: 'bg-n-amber-3 text-n-amber-11',
    processing: 'bg-n-blue-3 text-n-blue-11',
    running: 'bg-n-blue-3 text-n-blue-11',
    active: 'bg-n-blue-3 text-n-blue-11',
    ignored: 'bg-n-slate-3 text-n-slate-11',
    skipped: 'bg-n-slate-3 text-n-slate-11',
    received: 'bg-n-slate-3 text-n-slate-11',
    pending: 'bg-n-slate-3 text-n-slate-11',
    cancelled: 'bg-n-slate-3 text-n-slate-11',
    unknown: 'bg-n-slate-3 text-n-slate-11',
  };
  return classes[status] || classes.received;
};

const formatDateTime = value => {
  if (!value) return t('AUTOMATION_EVENTS.EMPTY_VALUE');
  return (
    formatJourneyDateTime(value, locale.value) ||
    t('AUTOMATION_EVENTS.EMPTY_VALUE')
  );
};

const formatDayLabel = dayKey => {
  if (!dayKey || dayKey === 'unknown') {
    return t('JORNADA_360.UNKNOWN_DAY');
  }
  const date = new Date(`${dayKey}T12:00:00Z`);
  return date.toLocaleDateString(normalizeJourneyLocale(locale.value), {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatMoney = (cents, currency = 'BRL') =>
  formatJourneyMoney(cents, currency, locale.value);

const stepsText = item => {
  const summary = item.stepsSummary || {};
  const total = summary.total || 0;
  if (!total) {
    const actorName = localizedJourneyActor(item.actor, t);
    if (actorName) return actorName;
    return t('AUTOMATION_EVENTS.TIMELINE.NO_STEPS');
  }
  const byStatus = summary.by_status || {};
  return Object.entries(byStatus)
    .map(([status, count]) =>
      t('AUTOMATION_EVENTS.TIMELINE.STEP_STATUS_COUNT', {
        count,
        status: statusLabel(status),
      })
    )
    .join(' · ');
};

const closeDetail = () => {
  activeDetailRequestId += 1;
  isDetailOpen.value = false;
  selectedEvent.value = null;
  isDetailLoading.value = false;
};

const openConversation = item => {
  // Only navigate with conversation_display_id — never the internal PK.
  if (!item?.conversationDisplayId || !accountId.value) return;
  router.push(
    frontendURL(
      conversationUrl({
        accountId: accountId.value,
        id: item.conversationDisplayId,
      })
    )
  );
  close();
};

/**
 * Execution detail is available only for automation_event resources.
 * Lookup uses links.resource_id (numeric DB id), never the Journey stable id.
 */
const openExecution = async item => {
  if (!item?.canViewExecution || item.resourceId == null) return;

  activeDetailRequestId += 1;
  const requestId = activeDetailRequestId;
  isDetailOpen.value = true;
  isDetailLoading.value = true;
  selectedEvent.value = item?.raw || item;
  try {
    const { data } = await AutomationEventsAPI.show(item.resourceId);
    if (requestId !== activeDetailRequestId) return;
    selectedEvent.value = data?.automation_event || item?.raw || item;
  } catch {
    if (requestId !== activeDetailRequestId) return;
    selectedEvent.value = item?.raw || item;
  } finally {
    if (requestId === activeDetailRequestId) isDetailLoading.value = false;
  }
};

const onEscapeKey = event => {
  if (event.key !== 'Escape' || !props.modelValue) return;
  event.stopPropagation();
  if (isDetailOpen.value) {
    closeDetail();
    return;
  }
  close();
};

let escapeListenerAttached = false;

const attachEscapeListener = () => {
  if (escapeListenerAttached || typeof document === 'undefined') return;
  document.addEventListener('keydown', onEscapeKey);
  escapeListenerAttached = true;
};

const detachEscapeListener = () => {
  if (!escapeListenerAttached || typeof document === 'undefined') return;
  document.removeEventListener('keydown', onEscapeKey);
  escapeListenerAttached = false;
};

watch(
  () => props.modelValue,
  open => {
    if (open) {
      activeCategory.value = 'all';
      visibleCount.value = INITIAL_VISIBLE;
      expandedIds.value = new Set();
      attachEscapeListener();
    } else {
      closeDetail();
      detachEscapeListener();
    }
  },
  { immediate: true }
);

// After remote append, ensure newly loaded rows enter the local window.
watch(
  () => props.items.length,
  (next, prev) => {
    if (next > (prev || 0) && visibleCount.value < next) {
      visibleCount.value = Math.max(
        visibleCount.value,
        Math.min(next, visibleCount.value + PAGE_SIZE)
      );
    }
  }
);

onBeforeUnmount(() => {
  detachEscapeListener();
});

defineExpose({
  isDetailOpen,
  onEscapeKey,
  closeDetail,
  showMore,
  canShowMore,
});
</script>

<template>
  <Teleport to="body" :disabled="!teleport">
    <div
      v-show="modelValue"
      class="jornada-360-drawer fixed inset-0 z-[60] flex justify-end"
      role="dialog"
      aria-modal="true"
      :aria-label="t('JORNADA_360.TITLE')"
    >
      <button
        type="button"
        class="absolute inset-0 bg-black/40 md:bg-black/25"
        :aria-label="t('JORNADA_360.ACTIONS.CLOSE')"
        @click="close"
      />

      <aside
        class="relative flex h-full w-full flex-col border-l border-n-weak bg-n-solid-1 shadow-xl md:w-[min(480px,calc(100vw-20rem))]"
        data-testid="jornada-360-drawer"
      >
        <header
          class="flex shrink-0 items-start justify-between gap-3 border-b border-n-weak px-4 py-3"
        >
          <div class="min-w-0">
            <div class="flex items-center gap-2">
              <span class="i-ri-planet-line size-4 shrink-0 text-n-brand" />
              <h2 class="truncate text-sm font-semibold text-n-slate-12">
                {{ t('JORNADA_360.TITLE') }}
              </h2>
            </div>
            <p
              v-if="contactName"
              class="mt-0.5 truncate text-xs text-n-slate-11"
            >
              {{ contactName }}
            </p>
          </div>
          <div class="flex shrink-0 items-center gap-1">
            <button
              type="button"
              class="flex size-8 items-center justify-center rounded-md text-n-slate-11 hover:bg-n-alpha-2"
              :aria-label="t('AUTOMATION_EVENTS.ACTIONS.REFRESH')"
              @click="emit('refresh')"
            >
              <span class="i-ri-refresh-line size-3.5" />
            </button>
            <button
              type="button"
              class="flex size-8 items-center justify-center rounded-md text-n-slate-11 hover:bg-n-alpha-2"
              :aria-label="t('JORNADA_360.ACTIONS.CLOSE')"
              data-testid="jornada-360-close"
              @click="close"
            >
              <span class="i-ri-close-line size-4" />
            </button>
          </div>
        </header>

        <div
          v-if="summary.score != null || summary.stage || summary.value"
          class="flex shrink-0 flex-wrap gap-2 border-b border-n-weak px-4 py-2.5"
        >
          <span
            v-if="summary.score != null"
            class="rounded-md bg-n-blue-3 px-2 py-1 text-[11px] font-medium text-n-blue-11"
          >
            {{ t('JORNADA_360.SUMMARY.SCORE', { score: summary.score }) }}
          </span>
          <span
            v-if="summary.stage"
            class="rounded-md bg-n-amber-3 px-2 py-1 text-[11px] font-medium text-n-amber-11"
          >
            {{ t('JORNADA_360.SUMMARY.STAGE', { stage: summary.stage }) }}
          </span>
          <span
            v-if="summary.value"
            class="rounded-md bg-n-teal-3 px-2 py-1 text-[11px] font-medium text-n-teal-11"
          >
            {{
              t('JORNADA_360.SUMMARY.VALUE', {
                value: formatMoney(
                  summary.value.totalCents,
                  summary.value.currency
                ),
              })
            }}
          </span>
        </div>

        <div
          class="flex shrink-0 gap-1 overflow-x-auto border-b border-n-weak px-3 py-2"
          role="tablist"
        >
          <button
            v-for="category in categories"
            :key="category.key"
            type="button"
            role="tab"
            class="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors"
            :class="
              activeCategory === category.key
                ? 'bg-n-brand text-white'
                : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
            "
            :aria-selected="activeCategory === category.key"
            :data-testid="`jornada-360-tab-${category.key}`"
            @click="onCategoryChange(category.key)"
          >
            {{ category.label }}
            <span class="ml-1 opacity-80">{{ category.count }}</span>
          </button>
        </div>

        <div class="min-h-0 flex-1 overflow-y-auto">
          <div v-if="isLoading" class="space-y-2 px-4 py-4">
            <div
              v-for="index in 4"
              :key="index"
              class="h-20 animate-pulse rounded-xl bg-n-alpha-2"
            />
          </div>

          <p
            v-else-if="error && !items.length"
            class="px-4 py-6 text-xs text-n-ruby-11"
          >
            {{ error }}
          </p>

          <div
            v-else-if="!filteredItems.length"
            class="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center"
          >
            <span class="i-ri-planet-line size-8 text-n-slate-9" />
            <p class="m-0 text-sm text-n-slate-12">
              {{ t('JORNADA_360.EMPTY') }}
            </p>
            <p class="m-0 text-xs text-n-slate-11">
              {{ t('JORNADA_360.EMPTY_HINT') }}
            </p>
          </div>

          <div v-else class="px-3 py-3">
            <p
              v-if="error"
              class="mb-3 rounded-md bg-n-amber-3 px-3 py-2 text-xs text-n-amber-11"
            >
              {{ error }}
            </p>

            <section
              v-for="group in dayGroups"
              :key="group.dayKey"
              class="mb-4 last:mb-0"
            >
              <h3
                class="sticky top-0 z-10 mb-2 bg-n-solid-1 px-1 py-1 text-[11px] font-semibold uppercase tracking-wide text-n-slate-11"
              >
                {{
                  t('JORNADA_360.META.DAY_COUNT', {
                    day: formatDayLabel(group.dayKey),
                    count: group.count,
                  })
                }}
              </h3>

              <article
                v-for="item in group.items"
                :key="item.id"
                class="mb-2 rounded-xl border border-n-weak bg-n-solid-2 px-3 py-2.5 last:mb-0"
                :data-testid="`jornada-360-item-${item.id}`"
              >
                <div class="flex items-start justify-between gap-2">
                  <div class="min-w-0">
                    <div class="truncate text-xs font-semibold text-n-slate-12">
                      {{ localizedTitle(item) }}
                    </div>
                    <div
                      class="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-n-slate-10"
                    >
                      <span>{{ formatDateTime(item.occurredAt) }}</span>
                      <span v-if="item.product">{{ item.product }}</span>
                      <span v-if="item.amountCents != null">
                        {{ formatMoney(item.amountCents, item.currency) }}
                      </span>
                      <span
                        v-if="item.apiCategory"
                        class="rounded bg-n-alpha-2 px-1 py-0.5 text-[10px] uppercase tracking-wide"
                      >
                        {{ item.apiCategory }}
                      </span>
                    </div>
                  </div>
                  <span
                    class="shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium"
                    :class="statusClass(item.status)"
                  >
                    {{ statusLabel(item.status) }}
                  </span>
                </div>

                <p class="mt-1.5 text-[11px] text-n-slate-11">
                  {{ stepsText(item) }}
                </p>
                <p
                  v-if="item.currentError"
                  class="mt-1 line-clamp-2 text-[11px] text-n-ruby-11"
                >
                  {{ item.currentError }}
                </p>

                <div class="mt-2 flex flex-wrap items-center gap-2">
                  <button
                    v-if="item.canViewExecution"
                    type="button"
                    class="inline-flex items-center gap-1 text-xs font-medium text-n-brand hover:underline"
                    data-testid="jornada-360-view-execution"
                    @click="openExecution(item)"
                  >
                    <span class="i-ri-file-search-line size-3.5" />
                    {{ t('JORNADA_360.ACTIONS.VIEW_EXECUTION') }}
                  </button>
                  <button
                    v-if="item.conversationDisplayId"
                    type="button"
                    class="inline-flex items-center gap-1 text-xs font-medium text-n-brand hover:underline"
                    data-testid="jornada-360-open-conversation"
                    @click="openConversation(item)"
                  >
                    <span class="i-ri-chat-3-line size-3.5" />
                    {{ t('JORNADA_360.ACTIONS.OPEN_CONVERSATION') }}
                  </button>
                  <button
                    type="button"
                    class="inline-flex items-center gap-1 text-xs text-n-slate-11 hover:text-n-slate-12"
                    data-testid="jornada-360-toggle-expand"
                    @click="toggleExpanded(item.id)"
                  >
                    <span
                      class="size-3.5"
                      :class="
                        isExpanded(item.id)
                          ? 'i-ri-arrow-up-s-line'
                          : 'i-ri-arrow-down-s-line'
                      "
                    />
                    {{
                      isExpanded(item.id)
                        ? t('JORNADA_360.ACTIONS.COLLAPSE')
                        : t('JORNADA_360.ACTIONS.EXPAND')
                    }}
                  </button>
                </div>

                <div
                  v-if="isExpanded(item.id)"
                  class="mt-2 space-y-1 rounded-md bg-n-alpha-2 px-2.5 py-2 text-[11px] text-n-slate-11"
                >
                  <div v-if="item.launchId">
                    {{
                      t('AUTOMATION_EVENTS.TIMELINE.LAUNCH', {
                        launch: item.launchId,
                      })
                    }}
                  </div>
                  <div v-if="item.transactionId">
                    {{
                      t('JORNADA_360.FIELDS.TRANSACTION_VALUE', {
                        label: t('AUTOMATION_EVENTS.FIELDS.TRANSACTION'),
                        value: item.transactionId,
                      })
                    }}
                  </div>
                  <div v-if="item.category">
                    {{
                      t('JORNADA_360.FIELDS.FIELD_VALUE', {
                        label: t('JORNADA_360.FIELDS.CATEGORY'),
                        value: localizedJourneyCategory(item.category, t),
                      })
                    }}
                  </div>
                  <div v-if="item.apiCategory">
                    {{
                      t('JORNADA_360.FIELDS.FIELD_VALUE', {
                        label: t('JORNADA_360.FIELDS.SOURCE_CATEGORY'),
                        value: item.apiCategory,
                      })
                    }}
                  </div>
                  <template v-if="isAdmin">
                    <div v-if="item.externalEventId">
                      {{
                        t('JORNADA_360.FIELDS.FIELD_VALUE', {
                          label: t(
                            'AUTOMATION_EVENTS.FIELDS.EXTERNAL_EVENT_ID'
                          ),
                          value: item.externalEventId,
                        })
                      }}
                    </div>
                    <div v-if="item.idempotencyKey">
                      {{
                        t('JORNADA_360.FIELDS.FIELD_VALUE', {
                          label: t('AUTOMATION_EVENTS.FIELDS.IDEMPOTENCY_KEY'),
                          value: item.idempotencyKey,
                        })
                      }}
                    </div>
                    <div v-if="item.processedAt">
                      {{
                        t('JORNADA_360.FIELDS.FIELD_VALUE', {
                          label: t('AUTOMATION_EVENTS.FIELDS.PROCESSED_AT'),
                          value: formatDateTime(item.processedAt),
                        })
                      }}
                    </div>
                    <div>
                      {{
                        t('JORNADA_360.FIELDS.FIELD_VALUE', {
                          label: t('JORNADA_360.FIELDS.EVENT_ID'),
                          value: item.id,
                        })
                      }}
                    </div>
                  </template>
                </div>
              </article>
            </section>

            <div v-if="canShowMore" class="pt-2 text-center">
              <button
                type="button"
                class="inline-flex items-center gap-1 rounded-md border border-n-weak px-3 py-1.5 text-xs font-medium text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-60"
                data-testid="jornada-360-show-more"
                :disabled="isLoadingMore"
                @click="showMore"
              >
                <span
                  v-if="isLoadingMore"
                  class="i-ri-loader-2-line size-3.5 animate-spin"
                />
                {{
                  expansion.hasMore
                    ? t('JORNADA_360.ACTIONS.SHOW_MORE', {
                        count: expansion.hiddenCount,
                      })
                    : t('JORNADA_360.ACTIONS.SHOW_MORE', {
                        count: '…',
                      })
                }}
              </button>
            </div>
          </div>
        </div>
      </aside>

      <AutomationEventDetailModal
        v-model="isDetailOpen"
        :event="selectedEvent"
        :is-loading="isDetailLoading"
      />
    </div>
  </Teleport>
</template>
